import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Warning,
  Error,
  ArrowUpward,
  ArrowDownward,
  Close,
  CalendarToday,
  LocalShipping,
  Schedule,
  Download
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const OnTimeDeliveryTab = ({ shipments = [], lateShipsCol, viewDetails }) => {
  const [selectedShipmentType, setSelectedShipmentType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [lateShipmentsDialogOpen, setLateShipmentsDialogOpen] = useState(false);

  // Sorting state for main table
  const [sortColumn, setSortColumn] = useState('totalShipments');
  const [sortDirection, setSortDirection] = useState('desc');

  // Sorting state for late shipments dialog
  const [lateSortColumn, setLateSortColumn] = useState('daysLate');
  const [lateSortDirection, setLateSortDirection] = useState('desc');

  // Handle column sorting for main table
  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get sort icon for main table
  const getSortIcon = column => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpward fontSize='small' />
    ) : (
      <ArrowDownward fontSize='small' />
    );
  };

  // Handle sorting for late shipments table
  const handleLateShipmentSort = column => {
    if (lateSortColumn === column) {
      setLateSortDirection(lateSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLateSortColumn(column);
      setLateSortDirection('desc');
    }
  };

  // Get sort icon for late shipments table
  const getLateSortIcon = column => {
    if (lateSortColumn !== column) return null;
    return lateSortDirection === 'asc' ? (
      <ArrowUpward fontSize='small' />
    ) : (
      <ArrowDownward fontSize='small' />
    );
  };

  // Function to calculate transit days based on pickup and delivery dates
  const calculateTransitDays = (pickupDate, deliveryDate) => {
    if (!pickupDate || !deliveryDate) return null;

    const parseDate = dateString => {
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    };

    const pickup = parseDate(pickupDate);
    const delivery = parseDate(deliveryDate);

    // Calculate difference in milliseconds
    const diffMs = delivery - pickup;

    // Convert to days (inclusive counting: pickup day = day 1)
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    // Return inclusive days
    return diffDays >= 0 ? diffDays + 1 : 0;
  };

  // Export main table to Excel
  const exportMainTableToExcel = () => {
    if (!calculateOnTimeDelivery.carrierData.length) {
      alert('No data to export!');
      return;
    }

    try {
      // Prepare data for export
      const exportData = calculateOnTimeDelivery.carrierData.map(carrier => ({
        'Carrier Name': carrier.carrierName,
        'Total Shipments': carrier.totalShipments,
        '% of Total': carrier.percentOfTotal,
        'Late Shipments': carrier.lateShipments,
        'On-Time Percentage': carrier.onTimePercentage,
        'Average Late Days': carrier.avgLateDays,
        'Grace Period Used': carrier.gracePeriodUsed,
        'Grace Period %': carrier.gracePeriodPercent,
        'Early Deliveries': carrier.earlyDeliveries,
        'On-Time Shipments': carrier.onTimeShipments,
        Status:
          carrier.status === 'green'
            ? 'Excellent (95-100%)'
            : carrier.status === 'yellow'
            ? 'Good (80-94%)'
            : 'Poor (<80%)'
      }));

      // Add summary row if available
      if (calculateOnTimeDelivery.summary) {
        const summary = calculateOnTimeDelivery.summary;
        exportData.push({});
        exportData.push({
          'Carrier Name': 'SUMMARY',
          'Total Shipments': summary.totalShipments,
          '% of Total': '100%',
          'Late Shipments': summary.totalLateShipments,
          'On-Time Percentage': `${summary.onTimePercentage}%`,
          'Average Late Days': '-',
          'Grace Period Used': summary.totalGracePeriodUsed,
          'Grace Period %': `${(
            (summary.totalGracePeriodUsed / summary.totalShipments) *
            100
          ).toFixed(1)}%`,
          'Early Deliveries': summary.totalEarlyDeliveries,
          'On-Time Shipments': summary.totalOnTimeShipments,
          Status: '-'
        });
      }

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = [
        { wch: 25 }, // Carrier Name
        { wch: 15 }, // Total Shipments
        { wch: 12 }, // % of Total
        { wch: 15 }, // Late Shipments
        { wch: 18 }, // On-Time Percentage
        { wch: 18 }, // Average Late Days
        { wch: 18 }, // Grace Period Used
        { wch: 15 }, // Grace Period %
        { wch: 15 }, // Early Deliveries
        { wch: 18 }, // On-Time Shipments
        { wch: 20 } // Status
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Carrier Performance');

      // Add metadata sheet
      const metadata = [
        ['Report: On-Time Delivery Performance'],
        ['Generated: ', new Date().toLocaleString()],
        ['Shipment Type: ', selectedShipmentType],
        ['Search Term: ', searchTerm || 'None'],
        ['Total Carriers: ', calculateOnTimeDelivery.carrierData.length],
        ['', ''],
        ...(calculateOnTimeDelivery.summary
          ? [
              [
                'Overall On-Time %: ',
                `${calculateOnTimeDelivery.summary.onTimePercentage}%`
              ],
              [
                'Total Shipments: ',
                calculateOnTimeDelivery.summary.totalShipments
              ],
              [
                'On-Time Shipments: ',
                calculateOnTimeDelivery.summary.totalOnTimeShipments
              ],
              [
                'Late Shipments: ',
                calculateOnTimeDelivery.summary.totalLateShipments
              ],
              [
                'Grace Period Used: ',
                calculateOnTimeDelivery.summary.totalGracePeriodUsed
              ],
              [
                'Early Deliveries: ',
                calculateOnTimeDelivery.summary.totalEarlyDeliveries
              ]
            ]
          : [])
      ];

      const wsMetadata = XLSX.utils.aoa_to_sheet(metadata);
      XLSX.utils.book_append_sheet(wb, wsMetadata, 'Report Info');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array'
      });

      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });

      // Save file
      const fileName = `Carrier_Performance_${selectedShipmentType}_${
        new Date().toISOString().split('T')[0]
      }.xlsx`;
      saveAs(data, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export late shipments to Excel
  const exportLateShipmentsToExcel = (carrierName, shipments) => {
    if (!carrierName || !shipments || shipments.length === 0) {
      alert('No late shipments data to export!');
      return;
    }

    try {
      // Prepare data for export
      const exportData = shipments.map((shipment, index) => ({
        '#': index + 1,
        'Shipment ID': shipment.shipmentId || `SHIP-${index + 1}`,
        Customer: shipment.proNumber || 'N/A',
        'Pickup Date': shipment.formattedPickupDate,
        'Actual Delivery': shipment.formattedActualDate,
        'Transit Days': shipment.actualTransitDays,
        'Days Late': shipment.daysLateBeyondGrace,
        'Hours Late': Math.round(shipment.diffHours),
        'Delay Severity':
          shipment.daysLateBeyondGrace > 7
            ? 'Significant (>7 days)'
            : shipment.daysLateBeyondGrace > 3
            ? 'Moderate (4-7 days)'
            : 'Minor (1-3 days)',
        Carrier: carrierName,
        'Shipment Type': shipment.shipmentType || 'Standard',
        Notes: shipment.notes || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = [
        { wch: 8 }, // #
        { wch: 15 }, // Shipment ID
        { wch: 20 }, // Customer
        { wch: 15 }, // Pickup Date
        { wch: 18 }, // Actual Delivery
        { wch: 15 }, // Transit Days
        { wch: 12 }, // Days Late
        { wch: 12 }, // Hours Late
        { wch: 18 }, // Delay Severity
        { wch: 20 }, // Carrier
        { wch: 15 }, // Shipment Type
        { wch: 30 } // Notes
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Late Shipments');

      // Add summary sheet
      const totalDaysLate = shipments.reduce(
        (sum, s) => sum + s.daysLateBeyondGrace,
        0
      );
      const avgDaysLate = (totalDaysLate / shipments.length).toFixed(1);
      const maxDaysLate = Math.max(
        ...shipments.map(s => s.daysLateBeyondGrace)
      );

      const summaryData = [
        ['Late Shipments Summary'],
        [''],
        ['Carrier:', carrierName],
        ['Generated:', new Date().toLocaleString()],
        ['Total Late Shipments:', shipments.length],
        ['Total Late Days:', totalDaysLate],
        ['Average Days Late:', avgDaysLate],
        ['Maximum Days Late:', maxDaysLate],
        [''],
        [
          'This report shows proof of late deliveries for carrier performance review.'
        ]
      ];

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array'
      });

      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });

      // Save file
      const fileName = `Late_Shipments_${carrierName.replace(
        /[^a-z0-9]/gi,
        '_'
      )}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(data, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Function to get detailed late shipments for a carrier
  const getLateShipmentsDetails = carrierName => {
    // Filter by shipment type first
    const typeFiltered =
      selectedShipmentType === 'All'
        ? shipments
        : shipments.filter(s => s.shipmentType === selectedShipmentType);

    // Get only delivered shipments with valid dates for this carrier
    const carrierShipments = typeFiltered.filter(
      s =>
        s.carrierName === carrierName &&
        s.shipmentStatus === 'Delivered' &&
        s.actualDeliveryDate &&
        s.actualPickupDate
    );

    // Parse dates function
    const parseDate = dateString => {
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    };

    const MS_PER_HOUR = 1000 * 60 * 60;

    // Process each shipment to determine if it's late
    const lateShipments = carrierShipments
      .map(shipment => {
        const pickupDate = parseDate(shipment.actualPickupDate);
        const actualDeliveryDate = parseDate(shipment.actualDeliveryDate);

        // Calculate actual transit days
        const actualTransitDays = calculateTransitDays(
          shipment.actualPickupDate,
          shipment.actualDeliveryDate
        );

        // For comparison, we need expected transit days - if not available, we'll use a baseline
        // This could be from promised transit days or a standard benchmark
        let expectedTransitDays = calculateTransitDays(
          shipment.actualPickupDate,
          shipment.promisedDeliveryDate
        );

        // Calculate expected delivery date
        const expectedDeliveryDate = new Date(pickupDate);
        expectedDeliveryDate.setDate(
          pickupDate.getDate() + parseInt(expectedTransitDays) - 1
        );

        // Calculate time difference in hours
        const diffMs = actualDeliveryDate - expectedDeliveryDate;
        const diffHours = diffMs / MS_PER_HOUR;

        // NEW LOGIC: Consider 24-hour grace period after expected date
        const isLate = diffHours > 24;
        const usedGracePeriod = diffHours > 0 && diffHours <= 24;

        if (isLate) {
          const daysLateBeyondGrace = Math.floor((diffHours - 24) / 24);

          return {
            ...shipment,
            pickupDate,
            actualDeliveryDate,
            expectedDeliveryDate,
            expectedTransitDays: parseInt(expectedTransitDays),
            actualTransitDays,
            diffHours,
            diffMs,
            daysLateBeyondGrace,
            isLate: true,
            usedGracePeriod,
            formattedPickupDate: pickupDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC'
            }),
            formattedActualDate: actualDeliveryDate.toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC'
              }
            ),
            formattedExpectedDate: expectedDeliveryDate.toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC'
              }
            )
          };
        }
        return null;
      })
      .filter(shipment => shipment !== null);

    return lateShipments;
  };

  // Function to handle opening late shipments dialog
  const handleViewLateShipments = carrierName => {
    setSelectedCarrier(carrierName);
    setLateShipmentsDialogOpen(true);
  };

  // Calculate On-Time Delivery performance with transit days logic
  const calculateOnTimeDelivery = useMemo(() => {
    if (!shipments.length) return { carrierData: [], summary: null };

    // Constants for time calculations
    const MS_PER_HOUR = 1000 * 60 * 60;

    // 1️⃣ Filter by shipment type
    const typeFiltered =
      selectedShipmentType === 'All'
        ? shipments
        : shipments.filter(s => s.shipmentType === selectedShipmentType);

    // 2️⃣ FILTER: Only delivered shipments with actual dates
    const validShipments = typeFiltered.filter(
      s =>
        s.shipmentStatus === 'Delivered' &&
        s.actualDeliveryDate &&
        s.actualPickupDate
    );

    const carrierGroups = {};

    // Parse date function
    const parseDate = dateString => {
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    };

    // 3️⃣ LOGIC: Calculate based on actual transit days vs expected + 24-hour grace period
    validShipments.forEach(shipment => {
      const carrierName = shipment.carrierName;

      if (!carrierGroups[carrierName]) {
        carrierGroups[carrierName] = {
          totalShipments: 0,
          lateShipments: 0,
          onTimeShipments: 0,
          earlyDeliveries: 0,
          gracePeriodUsed: 0,
          totalLateDays: 0,
          totalGraceHours: 0,
          totalActualTransitDays: 0,
          totalExpectedTransitDays: 0
        };
      }

      carrierGroups[carrierName].totalShipments++;

      // Parse dates
      const pickupDate = parseDate(shipment.actualPickupDate);
      const actualDeliveryDate = parseDate(shipment.actualDeliveryDate);

      // Calculate actual transit days
      const actualTransitDays = calculateTransitDays(
        shipment.actualPickupDate,
        shipment.actualDeliveryDate
      );

      carrierGroups[carrierName].totalActualTransitDays += actualTransitDays;

      // Get expected transit days (from promised or standard)
      const expectedTransitDays = calculateTransitDays(
        shipment.actualPickupDate,
        shipment.promisedDeliveryDate
      );

      carrierGroups[carrierName].totalExpectedTransitDays +=
        parseInt(expectedTransitDays);

      // Calculate expected delivery date
      const expectedDeliveryDate = new Date(pickupDate);
      expectedDeliveryDate.setDate(
        pickupDate.getDate() + parseInt(expectedTransitDays) - 1
      );

      // Calculate time difference in hours
      const diffMs = actualDeliveryDate - expectedDeliveryDate;
      const diffHours = diffMs / MS_PER_HOUR;

      // CLASSIFICATION LOGIC:
      if (diffHours < 0) {
        // Early delivery (before expected date)
        carrierGroups[carrierName].earlyDeliveries++;
        carrierGroups[carrierName].onTimeShipments++;
      } else if (diffHours <= 24) {
        // On-time with grace period (0 to 24 hours after expected)
        carrierGroups[carrierName].onTimeShipments++;
        if (diffHours > 0) {
          carrierGroups[carrierName].gracePeriodUsed++;
          carrierGroups[carrierName].totalGraceHours += diffHours;
        }
      } else {
        // Late (more than 24 hours after expected)
        carrierGroups[carrierName].lateShipments++;
        // Calculate days late beyond the 24-hour grace period
        const daysLateBeyondGrace = Math.floor((diffHours - 24) / 24);
        carrierGroups[carrierName].totalLateDays += daysLateBeyondGrace;
      }
    });

    // 4️⃣ Totals
    const totalShipments = validShipments.length;
    const totalLateShipments = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.lateShipments,
      0
    );
    const totalOnTimeShipments = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.onTimeShipments,
      0
    );
    const totalGracePeriodUsed = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.gracePeriodUsed,
      0
    );
    const totalEarlyDeliveries = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.earlyDeliveries,
      0
    );

    const onTimePercentage =
      totalShipments > 0
        ? ((totalOnTimeShipments / totalShipments) * 100).toFixed(1)
        : 0;

    // 5️⃣ Prepare carrier rows
    let allCarrierData = Object.entries(carrierGroups).map(
      ([carrierName, data]) => {
        const percentOfTotal =
          totalShipments > 0
            ? ((data.totalShipments / totalShipments) * 100).toFixed(1)
            : 0;

        const onTimePercent =
          data.totalShipments > 0
            ? ((data.onTimeShipments / data.totalShipments) * 100).toFixed(1)
            : 0;

        const avgLateDays =
          data.lateShipments > 0
            ? (data.totalLateDays / data.lateShipments).toFixed(0)
            : 0;

        const gracePeriodPercent =
          data.totalShipments > 0
            ? ((data.gracePeriodUsed / data.totalShipments) * 100).toFixed(1)
            : 0;

        const avgActualTransitDays =
          data.totalShipments > 0
            ? (data.totalActualTransitDays / data.totalShipments).toFixed(1)
            : 0;

        const avgExpectedTransitDays =
          data.totalShipments > 0
            ? (data.totalExpectedTransitDays / data.totalShipments).toFixed(1)
            : 0;

        // Status logic
        let status = 'red';
        let statusColor = '#ffebee';
        let icon = <Error sx={{ color: '#f44336' }} />;

        const onTimePercentNum = parseFloat(onTimePercent);
        if (onTimePercentNum >= 95) {
          status = 'green';
          statusColor = '#e8f5e9';
          icon = <CheckCircle sx={{ color: '#4caf50' }} />;
        } else if (onTimePercentNum >= 80) {
          status = 'yellow';
          statusColor = '#fffde7';
          icon = <Warning sx={{ color: '#ff9800' }} />;
        }

        return {
          carrierName,
          totalShipments: data.totalShipments,
          percentOfTotal: `${percentOfTotal}%`,
          rawPercentOfTotal: parseFloat(percentOfTotal),
          lateShipments: data.lateShipments,
          onTimePercentage: `${onTimePercent}%`,
          rawOnTimePercent: onTimePercentNum,
          avgLateDays,
          rawAvgLateDays: parseFloat(avgLateDays),
          gracePeriodUsed: data.gracePeriodUsed,
          gracePeriodPercent: `${gracePeriodPercent}%`,
          earlyDeliveries: data.earlyDeliveries,
          avgActualTransitDays,
          avgExpectedTransitDays,
          status,
          statusColor,
          icon,
          onTimeShipments: data.onTimeShipments,
          shipsWithMissingDates: 0
        };
      }
    );

    // 6️⃣ Sorting
    allCarrierData.sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'carrierName':
          aValue = a.carrierName.toLowerCase();
          bValue = b.carrierName.toLowerCase();
          break;
        case 'totalShipments':
          aValue = a.totalShipments;
          bValue = b.totalShipments;
          break;
        case 'percentOfTotal':
          aValue = a.rawPercentOfTotal;
          bValue = b.rawPercentOfTotal;
          break;
        case 'lateShipments':
          aValue = a.lateShipments;
          bValue = b.lateShipments;
          break;
        case 'onTimePercentage':
          aValue = a.rawOnTimePercent;
          bValue = b.rawOnTimePercent;
          break;
        case 'avgLateDays':
          aValue = a.rawAvgLateDays;
          bValue = b.rawAvgLateDays;
          break;
        case 'gracePeriodUsed':
          aValue = a.gracePeriodUsed;
          bValue = b.gracePeriodUsed;
          break;
        default:
          aValue = a.totalShipments;
          bValue = b.totalShipments;
      }

      return sortDirection === 'asc'
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    });

    // 7️⃣ Search filter
    const filteredData = searchTerm
      ? allCarrierData.filter(c =>
          c.carrierName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : allCarrierData;

    return {
      carrierData: filteredData,
      summary: {
        totalShipments,
        totalLateShipments,
        totalOnTimeShipments,
        totalGracePeriodUsed,
        totalEarlyDeliveries,
        shipsWithMissingDates: typeFiltered.length - validShipments.length,
        onTimePercentage: parseFloat(onTimePercentage),
        selectedShipmentType
      }
    };
  }, [shipments, selectedShipmentType, searchTerm, sortColumn, sortDirection]);

  // Late Shipments Dialog Component
  const LateShipmentsDialog = () => {
    if (!selectedCarrier) return null;

    const lateShipments = getLateShipmentsDetails(selectedCarrier);
    const carrierData = calculateOnTimeDelivery.carrierData.find(
      c => c.carrierName === selectedCarrier
    );

    // Sort late shipments
    const sortedLateShipments = [...lateShipments].sort((a, b) => {
      let aValue, bValue;

      switch (lateSortColumn) {
        case 'shipmentId':
          aValue = a.shipmentId || '';
          bValue = b.shipmentId || '';
          break;
        case 'proNumber':
          aValue = a.proNumber || '';
          bValue = b.proNumber || '';
          break;
        case 'pickupDate':
          aValue = a.pickupDate.getTime();
          bValue = b.pickupDate.getTime();
          break;
        case 'actualDate':
          aValue = a.actualDeliveryDate.getTime();
          bValue = b.actualDeliveryDate.getTime();
          break;
        case 'daysLate':
          aValue = a.daysLateBeyondGrace;
          bValue = b.daysLateBeyondGrace;
          break;
        case 'transitDays':
          aValue = a.actualTransitDays;
          bValue = b.actualTransitDays;
          break;
        case 'hoursLate':
          aValue = a.diffHours;
          bValue = b.diffHours;
          break;
        default:
          aValue = a.daysLateBeyondGrace;
          bValue = b.daysLateBeyondGrace;
      }

      if (lateSortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return (
      <Dialog
        open={lateShipmentsDialogOpen}
        onClose={() => setLateShipmentsDialogOpen(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShipping />
            <Typography variant='h6'>
              Late Shipments Details - {selectedCarrier}
            </Typography>
            <Chip
              label={`${carrierData?.lateShipments || 0} Late Shipments`}
              color='error'
              size='small'
            />
          </Box>
          <IconButton
            aria-label='close'
            onClick={() => setLateShipmentsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500]
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Carrier Summary */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Total Shipments
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.totalShipments || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  On-Time Percentage
                </Typography>
                <Typography
                  variant='h6'
                  color={
                    carrierData?.rawOnTimePercent >= 95
                      ? 'success.main'
                      : carrierData?.rawOnTimePercent >= 80
                      ? 'warning.main'
                      : 'error.main'
                  }
                >
                  {carrierData?.onTimePercentage || '0%'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Average Late Days
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.avgLateDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Avg Transit Days
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.avgActualTransitDays || 0} days
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Detailed Late Shipments Table with Sorting */}
          {sortedLateShipments.length > 0 ? (
            <>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      {/* Shipment ID with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('shipmentId')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          Shipment ID
                          {getLateSortIcon('shipmentId')}
                        </Box>
                      </TableCell>

                      {/* Customer with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('proNumber')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          PRO#
                          {getLateSortIcon('proNumber')}
                        </Box>
                      </TableCell>

                      {/* Pickup Date with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('pickupDate')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <CalendarToday fontSize='small' />
                          Pickup
                          {getLateSortIcon('pickupDate')}
                        </Box>
                      </TableCell>

                      {/* Actual Delivery with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('actualDate')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <CalendarToday fontSize='small' />
                          Actual
                          {getLateSortIcon('actualDate')}
                        </Box>
                      </TableCell>

                      {/* Transit Days with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('transitDays')}
                        align='center'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Schedule fontSize='small' />
                          Transit Days
                          {getLateSortIcon('transitDays')}
                        </Box>
                      </TableCell>

                      {/* Days Late with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('daysLate')}
                        align='right'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'flex-end'
                          }}
                        >
                          <Schedule fontSize='small' />
                          Days Late
                          {getLateSortIcon('daysLate')}
                        </Box>
                      </TableCell>

                      {/* Expected vs Actual */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold'
                        }}
                        align='center'
                      >
                        Expected vs Actual
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLateShipments.map((shipment, index) => (
                      <TableRow
                        key={`${shipment.shipmentId}-${index}`}
                        sx={{
                          '&:hover': { backgroundColor: '#f9f9f9' },
                          backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                        }}
                      >
                        <TableCell>
                          <Typography variant='body2' fontWeight='500'>
                            {shipment.shipmentId || `SHIP-${index + 1}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {shipment.proNumber || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Avatar
                              sx={{ width: 24, height: 24, bgcolor: '#1976d2' }}
                            >
                              <CalendarToday sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Box>
                              <Typography variant='body2' fontWeight='500'>
                                {shipment.formattedPickupDate}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='textSecondary'
                              >
                                Pickup
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Avatar
                              sx={{ width: 24, height: 24, bgcolor: '#f44336' }}
                            >
                              <CalendarToday sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Box>
                              <Typography variant='body2' fontWeight='500'>
                                {shipment.formattedActualDate}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='textSecondary'
                              >
                                Actual
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={`${shipment.actualTransitDays} days`}
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Chip
                            label={`${shipment.daysLateBeyondGrace} days`}
                            size='small'
                            color='error'
                            variant='outlined'
                          />
                          <Typography
                            variant='caption'
                            display='block'
                            color='textSecondary'
                          >
                            {Math.round(shipment.diffHours)} hours late
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Box>
                            <Typography variant='body2'>
                              <strong>Expected:</strong>{' '}
                              {shipment.expectedTransitDays} days
                            </Typography>
                            <Typography variant='body2'>
                              <strong>Actual:</strong>{' '}
                              {shipment.actualTransitDays} days
                            </Typography>
                            <Chip
                              label={`${
                                shipment.actualTransitDays -
                                shipment.expectedTransitDays
                              } days diff`}
                              size='small'
                              color={
                                shipment.actualTransitDays >
                                shipment.expectedTransitDays
                                  ? 'error'
                                  : 'success'
                              }
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Late Shipments Summary */}
              <Paper sx={{ p: 2, mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='subtitle2'>
                    Late Shipments Analysis (Based on Transit Days)
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    Sorted by: {lateSortColumn} ({lateSortDirection})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Total Late Shipments
                    </Typography>
                    <Typography variant='h6'>
                      {sortedLateShipments.length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Total Late Days
                    </Typography>
                    <Typography variant='h6'>
                      {sortedLateShipments.reduce(
                        (sum, s) => sum + s.daysLateBeyondGrace,
                        0
                      )}{' '}
                      days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Avg Transit Days
                    </Typography>
                    <Typography variant='h6'>
                      {(
                        sortedLateShipments.reduce(
                          (sum, s) => sum + s.actualTransitDays,
                          0
                        ) / sortedLateShipments.length
                      ).toFixed(1)}{' '}
                      days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Avg Delay
                    </Typography>
                    <Typography variant='h6' color='error.main'>
                      {(
                        sortedLateShipments.reduce(
                          (sum, s) => sum + s.daysLateBeyondGrace,
                          0
                        ) / sortedLateShipments.length
                      ).toFixed(1)}{' '}
                      days
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  *Transit Days = Actual Delivery Date - Actual Pickup Date
                  (inclusive)
                  <br />
                  *24-hour grace period applied after expected delivery date
                </Typography>
              </Paper>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant='h6' color='textSecondary'>
                No Late Shipments Found
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                All shipments for {selectedCarrier} were delivered within
                transit days + 24-hour grace period
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLateShipmentsDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() =>
              exportLateShipmentsToExcel(selectedCarrier, sortedLateShipments)
            }
            disabled={!sortedLateShipments.length}
            startIcon={<Download />}
          >
            Export to Excel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Late Shipments Dialog */}
      <LateShipmentsDialog />

      {/* Status Legend */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography variant='body2' fontWeight='medium'>
            Status:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#4caf50' }} />
            <Typography variant='caption'>Excellent (95-100%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#ff9800' }} />
            <Typography variant='caption'>Good (80-94%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Error sx={{ color: '#f44336' }} />
            <Typography variant='caption'>Poor (&lt;80%)</Typography>
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}
          >
            <Typography variant='caption' color='textSecondary'>
              *Outbound LTL On Time Performance*
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Performance Table */}
      <Paper elevation={2} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            maxHeight: '55vh',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px'
            }
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '900px'
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: '#f5f5f5',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                {/* Carrier */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '22%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('carrierName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Carrier
                    {getSortIcon('carrierName')}
                  </Box>
                </th>

                {/* Total Shipments */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '14%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('totalShipments')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    Total Shipments
                    {getSortIcon('totalShipments')}
                  </Box>
                </th>

                {/* % of Total */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '12%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('percentOfTotal')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    % of Total
                    {getSortIcon('percentOfTotal')}
                  </Box>
                </th>

                {/* Late Shipments */}
                {lateShipsCol && (
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      width: '14%',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort('lateShipments')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5
                      }}
                    >
                      Late Shipments
                      {getSortIcon('lateShipments')}
                    </Box>
                  </th>
                )}

                {/* On-Time % */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '14%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('onTimePercentage')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    On-Time %{getSortIcon('onTimePercentage')}
                  </Box>
                </th>

                {/* Avg Late Days */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '14%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('avgLateDays')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    Avg Late Days
                    {getSortIcon('avgLateDays')}
                  </Box>
                </th>

                {/* Status */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '10%'
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {calculateOnTimeDelivery.carrierData.length > 0 ? (
                calculateOnTimeDelivery.carrierData.map((carrier, index) => (
                  <tr
                    key={carrier.carrierName}
                    style={{
                      backgroundColor: carrier.statusColor,
                      borderBottom: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor:
                          carrier.status === 'green'
                            ? '#d4edda'
                            : carrier.status === 'yellow'
                            ? '#fff3cd'
                            : '#f8d7da'
                      }
                    }}
                  >
                    <td
                      style={{
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      {carrier.carrierName}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      {carrier.totalShipments.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem'
                      }}
                    >
                      {carrier.percentOfTotal}
                    </td>
                    {lateShipsCol && (
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '0.875rem',
                          color:
                            carrier.lateShipments > 0 ? '#d32f2f' : 'inherit'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1
                          }}
                        >
                          {carrier.lateShipments.toLocaleString()}
                          {carrier.lateShipments > 0 && viewDetails && (
                            <Button
                              size='small'
                              variant='outlined'
                              sx={{
                                ml: 1,
                                py: 0.25,
                                px: 1,
                                fontSize: '0.75rem',
                                minWidth: 'auto'
                              }}
                              onClick={() =>
                                handleViewLateShipments(carrier.carrierName)
                              }
                            >
                              View Details
                            </Button>
                          )}
                        </Box>
                      </td>
                    )}
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color:
                          carrier.rawOnTimePercent >= 95
                            ? '#2e7d32'
                            : carrier.rawOnTimePercent >= 80
                            ? '#ed6c02'
                            : '#c62828'
                      }}
                    >
                      {carrier.onTimePercentage}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem'
                      }}
                    >
                      {carrier.avgLateDays}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center'
                      }}
                    >
                      {carrier.icon}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#666'
                    }}
                  >
                    <Typography variant='body1'>
                      No carrier data found.
                    </Typography>
                    {searchTerm && (
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        sx={{ mt: 1 }}
                      >
                        Try searching for a different carrier name.
                      </Typography>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        {/* Summary Footer */}
        {calculateOnTimeDelivery.summary && (
          <Box
            sx={{
              p: 1,
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              minHeight: '48px'
            }}
          >
            {/* Left side info */}
            <Box sx={{ width: '31%', pl: 2 }}>
              <Typography variant='body2' color='textSecondary'>
                SUMMARY • {selectedShipmentType} shipments
                {searchTerm && ` • "${searchTerm}"`}
              </Typography>
            </Box>

            {/* Stats aligned with columns */}
            <Box sx={{ display: 'flex', flex: 1 }}>
              {/* Total Shipments column (14%) */}
              <Box
                sx={{
                  width: '16%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 0
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  Total:
                </Typography>
                <Typography variant='body2' fontWeight='600'>
                  {calculateOnTimeDelivery.summary.totalShipments}
                </Typography>
              </Box>

              {/* % of Total column (12%) */}
              <Box
                sx={{
                  width: '20%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 2
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  % of Total:
                </Typography>
                <Typography variant='body2' fontWeight='600'>
                  100%
                </Typography>
              </Box>

              {/* Late Shipments column (14% - only if shown) */}
              {lateShipsCol && (
                <Box
                  sx={{
                    width: '22%',
                    display: 'flex',
                    textAlign: 'right',
                    pr: 2
                  }}
                >
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    display='block'
                  >
                    Late:
                  </Typography>
                  <Typography variant='body2' fontWeight='600' color='#d32f2f'>
                    {calculateOnTimeDelivery.summary.totalLateShipments}
                  </Typography>
                </Box>
              )}

              {/* On-Time % column (14%) */}
              <Box
                sx={{
                  width: '14%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 2
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  On-Time %:
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  sx={{
                    color:
                      calculateOnTimeDelivery.summary.onTimePercentage >= 95
                        ? '#2e7d32'
                        : calculateOnTimeDelivery.summary.onTimePercentage >= 80
                        ? '#ed6c02'
                        : '#c62828'
                  }}
                >
                  {calculateOnTimeDelivery.summary.onTimePercentage}%
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Quick Stats */}
      {calculateOnTimeDelivery.carrierData.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'green'
              ).length
            } Excellent`}
            size='small'
            icon={<CheckCircle />}
            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
          />
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'yellow'
              ).length
            } Good`}
            size='small'
            icon={<Warning />}
            sx={{ backgroundColor: '#fffde7', color: '#ed6c02' }}
          />
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'red'
              ).length
            } Poor`}
            size='small'
            icon={<Error />}
            sx={{ backgroundColor: '#ffebee', color: '#c62828' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OnTimeDeliveryTab;
